import { IconNames, Option, Text, BaseOptionProps, Link, Button, Grid } from '@afterpaytouch/core'
import { Trans, useTranslation } from 'next-i18next'
import React, { FunctionComponent, useEffect, useState } from 'react'
import { Form } from '../Form'
import { Route } from '../../../config/router'
import { IdentityDocument, TransformedIdentityVerificationDocument, getVerifyDocumentRoute } from '../../../model'
import { useConsumerCountry, useIsPartialVerified, useConsumerVerifiedIdentityDocuments, useAppRouter, useConsumerName, useConsumerEmail } from '../../../state'
import styles from './styles.module.css'
import { useAmplitudeWithEnduringEventProperties } from '../../../integrations/amplitude'
import { TrackingEvent } from '../../../model/amplitude'
import { AmplitudeEventTypesProperties } from '../../../integrations/amplitude/types'
import { useAllowedDocumentsByCountry } from '../../../utils/id-check'
import { VerifyLaterModal } from '../VerifyLaterModal/VerifyLaterModal'
import { useConsumerSizes } from '../../../utils/responsive'
import { ProfileCard } from '../../ProfileCard'

interface Props {
  onSelect: (route: Route) => void
}

const I18N_NAMESPACE = ['common', 'verify', 'verifyIdentity']

const icons: Record<IdentityDocument, IconNames> = {
  licence: 'Car',
  medicare: 'Heartbeat',
  passport: 'Globe',
}

export const VerifyIdentity: FunctionComponent<Props> = ({ onSelect }) => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const country = useConsumerCountry()
  const verifiedIdentityDocuments = useConsumerVerifiedIdentityDocuments()
  const isPartialVerified = useIsPartialVerified()
  const amplitude = useAmplitudeWithEnduringEventProperties()
  const router = useAppRouter()
  const { buttonSize } = useConsumerSizes()
  const [showVerifyLaterModal, setShowVerifyLaterModal] = useState(false)
  const consumerName = useConsumerName()
  const consumerEmail = useConsumerEmail() ?? ''

  const getPressedButtonEventName = (doc: IdentityDocument): keyof AmplitudeEventTypesProperties => {
    return {
      licence: TrackingEvent.PRESSED_HARDID_LANDING_PAGE_DRIVERS_LICENCE_BUTTON,
      medicare: TrackingEvent.PRESSED_HARDID_LANDING_PAGE_MEDICARE_BUTTON,
      passport: TrackingEvent.PRESSED_HARDID_LANDING_PAGE_PASSPORT_BUTTON,
    }[doc]
  }

  const getPropsByDocument = (doc: IdentityDocument): BaseOptionProps => ({
    icon: icons[doc],
    text: t(`verifyIdentity:document.${doc}`),
    onClick: (e) => {
      e.preventDefault()
      amplitude.logEvent(getPressedButtonEventName(doc))
      onSelect(getVerifyDocumentRoute({ document: doc, country }))
    },
  })

  const transformedAllowedDocument = useAllowedDocumentsByCountry(country).filter(
    (doc) => !(verifiedIdentityDocuments?.includes(doc as TransformedIdentityVerificationDocument) ?? false)
  )

  const handleVerifyLaterClick = (): void => {
    amplitude.logEvent(TrackingEvent.PRESSED_VERIFY_LATER_BUTTON)
    setShowVerifyLaterModal(true)
  }

  const handleModalClose = (): void => {
    setShowVerifyLaterModal(false)
  }

  const handleVerifyNowClick = (): void => {
    amplitude.logEvent(TrackingEvent.PRESSED_VERIFY_NOW_BUTTON)
    setShowVerifyLaterModal(false)
  }

  const handleSkipClick = (): void => {
    amplitude.logEvent(TrackingEvent.PRESSED_SKIP_BUTTON)
    setShowVerifyLaterModal(false)
    router.push(Route.HOME)
  }

  useEffect(() => {
    if (transformedAllowedDocument.length === 1) {
      onSelect(getVerifyDocumentRoute({ document: transformedAllowedDocument[0], retry: isPartialVerified, country }))
    }
  }, [])

  return (
    <>
      <Form>
        <ProfileCard consumerName={consumerName} consumerEmail={consumerEmail} />
        <div className='mb-2 mt-2'>
          <Text>
            <Trans i18nKey={'verifyIdentity:description'} />
          </Text>
        </div>
        <div className={styles.documentOptions}>
          {transformedAllowedDocument.map((doc) => {
            const key = `verify-identity-${doc}`
            return <Option key={key} testNameSpace={key} {...getPropsByDocument(doc)} />
          })}
        </div>

        <Text>
          <Trans
            i18nKey='verifyIdentity:help'
            components={{
              a: (
                <Link
                  key='description-link'
                  target='_blank'
                  kind='Underline'
                  href={t('common:urls:identityHelpUrl')}
                  testNameSpace='verifyIdentity'
                  onClick={() => {
                    amplitude.logEvent(TrackingEvent.PRESSED_HARDID_LANDING_PAGE_HELP_LINK)
                  }}
                />
              ),
            }}
          />
        </Text>

        <Grid>
          <Button.Ghost testNameSpace='verify-identity-verify-later' onClick={handleVerifyLaterClick} padding='Fluid'>
            {t('verifyIdentity:verifyLater:buttonText')}
          </Button.Ghost>
        </Grid>
      </Form>

      <VerifyLaterModal
        show={showVerifyLaterModal}
        onClose={handleModalClose}
        onVerifyNow={handleVerifyNowClick}
        onSkip={handleSkipClick}
        testNameSpace='verify-later'
        buttonSize={buttonSize}
      />
    </>
  )
}
